import axios from 'axios';
import cookie from 'js-cookie';

// Base URL for floorplan API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Floorplan Service for managing floorplan data
 */
class FloorplanService {
    /**
     * Get all floorplans for a specific project
     * @param {string} projectId - The ID of the project
     * @returns {Promise<Array>} Array of floorplan objects
     */
    async getFloorplansByProject(projectId) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/floorplans/getFloorplansByProject/${projectId}`,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            // Transform backend data to match frontend expectations
            return response.data
                .map((plan) => ({
                    id: plan.floorplanId,
                    fileName: plan.name,
                    groupName: plan.groupName || null,
                    imageUrl: plan.imageUrl ? `${API_BASE_URL}/images/${plan.imageUrl}` : null,
                    projectId: plan.projectId,
                    dateCreated: plan.dateCreated,
                    dateUpdated: plan.dateUpdated,
                }))
                .sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Get a specific floorplan by ID
     * @param {string} floorplanId - The ID of the floorplan
     * @returns {Promise<Object>} Floorplan object
     */
    async getFloorplanById(floorplanId) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/floorplans/getFloorplanById/${floorplanId}`,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            // Transform backend data to match frontend expectations
            const plan = response.data;
            return {
                id: plan.floorplanId,
                fileName: plan.name,
                groupName: plan.groupName || null,
                imageUrl: plan.imageUrl ? `${API_BASE_URL}/images/${plan.imageUrl}` : null,
                description: plan.description,
                projectId: plan.projectId,
                dateCreated: plan.dateCreated,
                dateUpdated: plan.dateUpdated,
            };
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Create a new floorplan with image
     * @param {Object} floorplanData - Floorplan metadata
     * @param {File} imageFile - Image file to upload
     * @returns {Promise<Object>} Created floorplan object
     */
    async createFloorplan(floorplanData, imageFile) {
        try {
            const formData = new FormData();

            // Create floorplan DTO object (backend expects @RequestPart)
            const floorplanDTO = {
                projectId: floorplanData.projectId,
                name: floorplanData.name,
                groupName: floorplanData.groupName || null,
            };

            // Add floorplan metadata as JSON string (for @RequestPart)
            //formData.append('floorplan', JSON.stringify(floorplanDTO));
            formData.append(
                'floorplan',
                new Blob([JSON.stringify(floorplanDTO)], { type: 'application/json' })
            );
            formData.append('image', imageFile);

            const response = await axios.post(
                `${API_BASE_URL}/floorplans/createFloorplan`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                        // Don't set Content-Type, let browser set it with boundary for multipart
                    },
                }
            );

            // Transform backend data to match frontend expectations
            const plan = response.data;
            return {
                id: plan.floorplanId,
                fileName: plan.name,
                groupName: plan.groupName || null,
                imageUrl: plan.imageUrl ? `${API_BASE_URL}/images/${plan.imageUrl}` : null,
                projectId: plan.projectId,
                dateCreated: plan.dateCreated,
                dateUpdated: plan.dateUpdated,
            };
        } catch (error) {
            console.error('🏗️ floorplanService: Error creating floorplan:', error);
            throw this.handleAPIError(error);
        }
    }

    /**
     * Duplicate a floorplan and all its ROIs as an independent copy
     * @param {string} floorplanId - The ID of the floorplan to duplicate
     * @param {string} newName - The name for the duplicated floorplan
     * @returns {Promise<Object>} Duplicated floorplan object
     */
    async duplicateFloorplan(floorplanId, newName) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/floorplans/duplicate/${floorplanId}`,
                { name: newName },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            const plan = response.data;
            return {
                id: plan.floorplanId,
                fileName: plan.name,
                groupName: plan.groupName || null,
                imageUrl: plan.imageUrl ? `${API_BASE_URL}/images/${plan.imageUrl}` : null,
                projectId: plan.projectId,
                dateCreated: plan.dateCreated,
                dateUpdated: plan.dateUpdated,
            };
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Update a floorplan's metadata (name, groupName)
     * @param {string} floorplanId - The ID of the floorplan
     * @param {Object} updates - Fields to update
     * @returns {Promise<Object>} Updated floorplan object
     */
    async updateFloorplan(floorplanId, updates) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/floorplans/update/${floorplanId}`,
                updates,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            const plan = response.data;
            return {
                id: plan.floorplanId,
                fileName: plan.name,
                groupName: plan.groupName || null,
                imageUrl: plan.imageUrl ? `${API_BASE_URL}/images/${plan.imageUrl}` : null,
                projectId: plan.projectId,
                dateCreated: plan.dateCreated,
                dateUpdated: plan.dateUpdated,
            };
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Delete a floorplan
     * @param {string} floorplanId - The ID of the floorplan to delete
     * @returns {Promise<boolean>} Success status
     */
    async deleteFloorplan(floorplanId) {
        try {
            await axios.delete(`${API_BASE_URL}/floorplans/delete/${floorplanId}`, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            });

            return true;
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Handle API errors and provide user-friendly messages
     * @param {Error} error - The error object from axios
     * @returns {Error} Formatted error with user-friendly message
     */
    handleAPIError(error) {
        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const message =
                error.response.data?.message || error.response.data || 'Unknown server error';

            switch (status) {
                case 400:
                    return new Error(`Invalid request: ${message}`);
                case 401:
                    return new Error('Authentication required. Please log in again.');
                case 403:
                    return new Error('You do not have permission to perform this action.');
                case 404:
                    return new Error('Floorplan not found.');
                case 409:
                    return new Error('Floorplan conflict. Please refresh and try again.');
                case 500:
                    return new Error('Server error. Please try again later.');
                default:
                    return new Error(`Server error (${status}): ${message}`);
            }
        } else if (error.request) {
            // Network error
            return new Error('Network error. Please check your connection and try again.');
        } else {
            // Other error
            return new Error(error.message || 'An unexpected error occurred.');
        }
    }
}

// Create a singleton instance
const floorplanService = new FloorplanService();

export default floorplanService;
